import {
  InnspelApiError,
  type FollowUpView,
  type InnspelClient,
  type MyPrograms,
  type ParticipationView,
  type ProgramTask,
  type ProgramView,
  type TaskOutcome,
} from '@innspel/core';
import { useCallback, useEffect, useState } from 'react';
import { Banner, Btn, Card, H, Hint, Icon, P, Skeleton, useInnspel } from '../../lib/index.ts';
import type { PortalKonfig } from '../konfig.ts';
import { HvordanGikkDet, utfallEtikett } from './HvordanGikkDet.tsx';

type Dialog = { type: 'oppgave'; task: ProgramTask } | { type: 'oppfolging'; oppfolging: FollowUpView };

/** Kontakter med spørsmål tar svar; de andre er handlinger (tech-decisions «M3.2 (forts.)»). */
function tarSvar(kind: FollowUpView['kind']): boolean {
  return kind === 'hvordan_gikk_det' || kind === 'sjekkpunkt';
}

const MANEDER = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'] as const;

/** Perioden slik den står i aksepten (DD-32). Samme ordlyd som i appen — ett løfte, én form. */
function periode(fra: string, til: string): string {
  const f = new Date(fra);
  const t = new Date(til);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return '';
  return f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear()
    ? `${f.getDate()}.–${t.getDate()}. ${MANEDER[t.getMonth()]}`
    : `${f.getDate()}. ${MANEDER[f.getMonth()]} – ${t.getDate()}. ${MANEDER[t.getMonth()]}`;
}

function snittMinutter(program: ProgramView): number {
  if (program.tasks.length === 0) return 0;
  return Math.round(program.tasks.reduce((sum, t) => sum + t.estimatedMinutes, 0) / program.tasks.length);
}

/**
 * Betaprogram-fanen i portalen (M3.1): invitasjonen med forventningsavklaringen
 * (DD-13, DD-32), aksept, og «Gå ut» med bekreftelse (DD-36).
 *
 * Invitasjonstokenet er engangs og kom i LEIETAKERENS kanal (DD-12) — portalen leser
 * det fra `#inv=` i lenken. Uten det vises invitasjonen fortsatt, men «Bli med» er
 * inaktiv: det finnes ingen vei rundt tokenet, og en knapp som feiler ved trykk er
 * verre enn en som sier hvorfor.
 *
 * Deltakelsen (3c) viser oppgavene med «Gjort» → «Hvordan gikk det?» (tre lukkede valg
 * og en setning, aldri en skala) og oppfølgingskortene (3d, DD-34), der arten avgjør
 * knappene. «Ikke» tilbyr å melde feilen i portalens eget feilspor.
 */
export function Betaprogram({
  client,
  konfig,
  innlogget,
  invitasjonstoken,
  onAkseptert,
}: {
  client: InnspelClient;
  konfig: PortalKonfig;
  innlogget: boolean;
  /** Engangs-tokenet fra invitasjonslenken, hvis portalen ble åpnet med en. */
  invitasjonstoken?: string | null;
  /** Kalles når aksepten er lagret, så tokenet kan glemmes — det virker bare én gang. */
  onAkseptert?: () => void;
}) {
  const { open } = useInnspel();
  const [data, setData] = useState<MyPrograms | null>(null);
  const [deltakelse, setDeltakelse] = useState<ParticipationView | null>(null);
  const [laster, setLaster] = useState(innlogget);
  const [feil, setFeil] = useState(false);
  const [handlingsfeil, setHandlingsfeil] = useState<string | null>(null);
  const [sender, setSender] = useState(false);
  const [gaaUt, setGaaUt] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [svarFeil, setSvarFeil] = useState<string | null>(null);
  const [lagtBort, setLagtBort] = useState<ReadonlySet<string>>(new Set());
  const [meldFeilFor, setMeldFeilFor] = useState<ProgramTask | null>(null);

  const leggBort = useCallback((id: string) => setLagtBort((s) => new Set([...s, id])), []);

  // «Lagre» i «Hvordan gikk det?»: én dialog, to mål. Fullføringen legges på deltakelsen så
  // raden viser svaret straks; et svar på oppfølging tar kortet ut av lista.
  const lagreSvar = useCallback(
    async (outcome: TaskOutcome, text: string | undefined) => {
      if (!dialog || !deltakelse) return;
      setSender(true);
      setSvarFeil(null);
      try {
        if (dialog.type === 'oppgave') {
          const ny = await client.programs.completeTask(dialog.task.id, { outcome, ...(text ? { text } : {}) });
          setDeltakelse({ ...deltakelse, completions: [...deltakelse.completions, ny] });
          setMeldFeilFor(outcome === 'ikke' ? dialog.task : null);
        } else {
          await client.programs.respond(dialog.oppfolging.id, { outcome, ...(text ? { text } : {}) });
          setData((d) => (d ? { ...d, pendingFollowUps: d.pendingFollowUps.filter((f) => f.id !== dialog.oppfolging.id) } : d));
        }
        setDialog(null);
      } catch (e) {
        setSvarFeil(e instanceof InnspelApiError ? e.message : 'Tjeneren svarte ikke. Prøv igjen om litt.');
      } finally {
        setSender(false);
      }
    },
    [client, deltakelse, dialog],
  );

  useEffect(() => {
    if (!innlogget) return;
    let aktiv = true;
    setLaster(true);
    client.programs
      .list()
      .then((d) => {
        if (!aktiv) return;
        setData(d);
        setDeltakelse(d.participations.find((p) => p.status !== 'trukket') ?? null);
      })
      .catch(() => aktiv && setFeil(true))
      .finally(() => aktiv && setLaster(false));
    return () => {
      aktiv = false;
    };
  }, [client, innlogget]);

  const bliMed = useCallback(async () => {
    if (!invitasjonstoken) return;
    setSender(true);
    setHandlingsfeil(null);
    try {
      const ny = await client.programs.accept(invitasjonstoken);
      setDeltakelse(ny);
      onAkseptert?.();
    } catch (e) {
      setHandlingsfeil(e instanceof InnspelApiError ? e.message : 'Tjeneren svarte ikke. Prøv igjen om litt.');
    } finally {
      setSender(false);
    }
  }, [client, invitasjonstoken, onAkseptert]);

  const trekkDeg = useCallback(async () => {
    if (!deltakelse) return;
    setSender(true);
    try {
      await client.programs.withdraw(deltakelse.id);
      setDeltakelse(null);
      setGaaUt(false);
    } catch (e) {
      setHandlingsfeil(e instanceof InnspelApiError ? e.message : 'Tjeneren svarte ikke. Prøv igjen om litt.');
    } finally {
      setSender(false);
    }
  }, [client, deltakelse]);

  if (!innlogget) {
    return (
      <Banner ikon="key-outline" tone="info">
        Betaprogrammet finnes bare når du er logget inn hos {konfig.tenantName}.
      </Banner>
    );
  }

  const invitasjon = deltakelse ? undefined : data?.invitations[0];
  const program = deltakelse?.program ?? invitasjon?.program;

  return (
    <>
      {/*
        DD-14 presisert 2026-09-20: kontakttaket står i PANELET, aldri i det innsenderen
        leser. «Vi tar kontakt maks 3 ganger — 1 brukt» sto her ved siden av programmets
        navn; hun kan ikke gjøre noe med tallet, og det er en regel mellom Innspel og
        leietakeren. Det DD-13 krever — hva som testes, hvor lenge, og at oppfølgingene
        kommer — står fortsatt, på aksept-skjermen og som «Kontakt 2 av 3» på kortet hun
        faktisk har fått.
      */}
      <H nivaa={1} sub={deltakelse ? deltakelse.program.name : undefined}>
        Betaprogram
      </H>

      {handlingsfeil ? (
        <Banner ikon="warning-outline" tone="danger" live testId="beta-feil">
          {handlingsfeil}
        </Banner>
      ) : null}

      {laster ? (
        <div className="innspel-stabel" data-gap="sm" role="status" aria-label="Henter betaprogrammet">
          <Skeleton hoy />
          <Skeleton hoy />
        </div>
      ) : feil ? (
        <Banner ikon="warning-outline" tone="danger" live>
          Kunne ikke hente oppgavene. Prøv igjen om litt.
        </Banner>
      ) : !program ? (
        <Card dashed>
          <P>Du er ikke med i noe betaprogram nå.</P>
          <Hint>Får du en invitasjon fra {konfig.tenantName}, dukker den opp her. Du kan alltid sende et fritt innspill.</Hint>
        </Card>
      ) : deltakelse ? (
        <>
          {/* 3d: kortene for DETTE programmet, i den rekkefølgen kontaktene kom (DD-34). */}
          {(() => {
            const kort = (data?.pendingFollowUps ?? [])
              .filter((f) => f.programId === deltakelse.program.id && !lagtBort.has(f.id))
              .sort((a, b) => a.emittedAt.localeCompare(b.emittedAt));
            const forsteNr = Math.max(1, deltakelse.contactsSent - kort.length + 1);
            return kort.map((f, i) => {
              const art = tarSvar(f.kind) ? 'spoersmaal' : f.kind === 'tilbud_om_utgang' ? 'utgang' : 'paaminnelse';
              const nr = `Kontakt ${forsteNr + i} av ${deltakelse.program.maxContacts}`;
              return (
                <Card key={f.id} testId={`oppfolging-${f.id}`} role="group" ariaLabel="Oppfølging fra betaprogrammet">
                  <P>
                    <Icon navn={art === 'spoersmaal' ? 'flask-outline' : art === 'utgang' ? 'exit-outline' : 'calendar-outline'} /> {f.question}
                  </P>
                  <Hint>{art === 'spoersmaal' ? `${nr} · tar under ett minutt` : art === 'utgang' ? `${nr} · den siste vi sender` : `${nr} · planlagt påminnelse`}</Hint>
                  <div className="innspel-rad" data-gap="md">
                    {art === 'utgang' ? (
                      <>
                        <Btn grow variant="secondary" label="Bli værende" testId={`bli-vaerende-${f.id}`} onClick={() => leggBort(f.id)} />
                        <Btn grow variant="danger" ikon="exit-outline" label="Gå ut" testId={`gaa-ut-${f.id}`} onClick={() => setGaaUt(true)} />
                      </>
                    ) : (
                      <>
                        <Btn grow variant="secondary" label="Senere" testId={`senere-${f.id}`} onClick={() => leggBort(f.id)} />
                        {art === 'spoersmaal' ? (
                          <Btn
                            grow
                            label="Svar nå"
                            testId={`svar-naa-${f.id}`}
                            onClick={() => {
                              setSvarFeil(null);
                              setDialog({ type: 'oppfolging', oppfolging: f });
                            }}
                          />
                        ) : (
                          <Btn grow label="Åpne oppgavene" testId={`aapne-oppgavene-${f.id}`} onClick={() => leggBort(f.id)} />
                        )}
                      </>
                    )}
                  </div>
                </Card>
              );
            });
          })()}

          <ul className="innspel-liste" data-testid="oppgaver" aria-label="Oppgavene">
            {[...deltakelse.program.tasks]
              .sort((a, b) => a.order - b.order)
              .map((oppgave, i) => {
                const c = deltakelse.completions.find((x) => x.taskId === oppgave.id);
                return (
                  <li key={oppgave.id}>
                    <Card testId={`oppgave-${oppgave.id}`}>
                      <P>
                        {i + 1}. {oppgave.title}
                      </P>
                      <Hint>
                        {c ? `Prøvd · ${utfallEtikett(c.outcome)}` : `Ikke begynt · ca. ${oppgave.estimatedMinutes} min`}
                        {!c && oppgave.screenHint ? ` · ${oppgave.screenHint}` : ''}
                        {c?.text ? ` · «${c.text}»` : ''}
                      </Hint>
                      {!c ? (
                        <Btn
                          variant="secondary"
                          ikon="checkmark-circle-outline"
                          label="Gjort"
                          ariaLabel={`Gjort: ${oppgave.title}`}
                          testId={`gjort-${oppgave.id}`}
                          onClick={() => {
                            setSvarFeil(null);
                            setDialog({ type: 'oppgave', task: oppgave });
                          }}
                        />
                      ) : null}
                    </Card>
                  </li>
                );
              })}
          </ul>

          {dialog ? (
            <HvordanGikkDet
              under={dialog.type === 'oppgave' ? dialog.task.title : dialog.oppfolging.question}
              sender={sender}
              feil={svarFeil}
              onLagre={(outcome, text) => void lagreSvar(outcome, text)}
              onLukk={() => setDialog(null)}
            />
          ) : null}

          {meldFeilFor ? (
            <Card dashed testId="meld-feil-kort">
              <Hint>Gikk det ikke? Et innspill hjelper oss å finne ut hvorfor.</Hint>
              <Btn
                variant="secondary"
                ikon="bug-outline"
                label="Meld det som feil"
                testId="meld-feil"
                onClick={() => {
                  setMeldFeilFor(null);
                  void open();
                }}
              />
            </Card>
          ) : null}

          {/* DD-36: «Gå ut» er alltid synlig — aldri gjemt bak en meny. */}
          <Btn variant="ghost" ikon="exit-outline" label="Gå ut av programmet" testId="gaa-ut" onClick={() => setGaaUt(true)} />
        </>
      ) : (
        <Aksept program={program} harToken={Boolean(invitasjonstoken)} sender={sender} tenantName={konfig.tenantName} onBliMed={() => void bliMed()} />
      )}

      <Btn variant="secondary" ikon="chatbubble-outline" label="Send et fritt innspill" onClick={() => void open()} />

      {gaaUt ? (
        <Card testId="gaa-ut-dialog" role="group" ariaLabel="Bekreft at du vil gå ut av programmet">
          <H id="gaa-ut-h">Gå ut av programmet?</H>
          <P>
            Du tas ut av testgruppen med en gang. «{deltakelse?.program.name}» slås av for deg, og {konfig.tenantName} tar ikke kontakt igjen. Det du allerede har svart, beholdes.
          </P>
          <div className="innspel-rad" data-gap="md">
            {/* «Bli værende» først: utgangen er et valg, ikke en felle med angreknapp. */}
            <Btn grow variant="secondary" label="Bli værende" testId="bli-vaerende" onClick={() => setGaaUt(false)} />
            <Btn grow variant="danger" ikon="exit-outline" label="Gå ut" testId="bekreft-gaa-ut" disabled={sender} onClick={() => void trekkDeg()} />
          </div>
        </Card>
      ) : null}
    </>
  );
}

/** 3b — aksepten: tallene først (DD-32), så oppgavene, kjente feil og vilkårene som lenke (DD-37). */
function Aksept({ program, harToken, sender, tenantName, onBliMed }: { program: ProgramView; harToken: boolean; sender: boolean; tenantName: string; onBliMed: () => void }) {
  return (
    <div className="innspel-stabel" data-gap="md" data-testid="beta-aksept">
      <Card>
        <H sub="Hva testes">{program.name}</H>
        <P>
          {program.tasks.length} oppgaver · ca. {snittMinutter(program)} min hver · {periode(program.opensAt, program.closesAt)} · maks {program.maxContacts} kontakter
        </P>
        <P>{program.hypothesis}</P>
      </Card>

      <Card>
        <H>Hva vi ber om</H>
        <ul className="innspel-liste">
          {program.tasks.map((o, i) => (
            <li key={o.id}>
              <P>
                {i + 1}. {o.title}
              </P>
              <Hint>
                ca. {o.estimatedMinutes} min{o.screenHint ? ` · ${o.screenHint}` : ''}
              </Hint>
            </li>
          ))}
        </ul>
      </Card>

      {program.knownIssues.length > 0 ? (
        <Card>
          <H sub="Disse trenger du ikke melde — vi vet om dem.">{`Kjente feil (${program.knownIssues.length})`}</H>
          <ul className="innspel-liste">
            {program.knownIssues.map((k) => (
              <li key={k.id}>
                <P>{k.title}</P>
                {k.workaround ? <Hint>Omvei: {k.workaround}</Hint> : null}
                {k.fixedIn ? <Hint>{k.status === 'fikset' ? `Rettet i ${k.fixedIn}` : `Rettes i ${k.fixedIn}`}</Hint> : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card>
        <H>Hva som samles inn</H>
        <P>Nettleser, skjerm og hva du svarer. Aldri navn, e-post, telefon eller posisjon.</P>
        <Hint>{tenantName} vet hvem du er — Innspel gjør det ikke.</Hint>
      </Card>

      {/* DD-37: en lenke, aldri en avkryssing — og ingen vilkår i forpliktelsesfri form. */}
      {program.termsUrl && !program.commitmentFree ? (
        <p className="innspel-p">
          <a href={String(program.termsUrl)} target="_blank" rel="noreferrer" data-testid="vilkar-lenke">
            Les vilkårene <Icon navn="open-outline" />
          </a>
          <Hint>Bare en lenke. Ingen avkryssing, ingen signatur — «Bli med» binder deg ikke til noe.</Hint>
        </p>
      ) : (
        <Hint>Ingen vilkår, ingen avtale. Det eneste som gjelder: du kan gå ut når som helst.</Hint>
      )}

      <Btn label="Bli med" testId="bli-med" disabled={!harToken || sender} hint={harToken ? 'Samtykket gjelder bare dette programmet.' : `Åpne lenken ${tenantName} sendte deg for å bli med.`} onClick={onBliMed} />
    </div>
  );
}
